import type { CardCivilization } from "@dm-online/shared";
import { getCardById } from "@dm-online/shared";

// 文明の表示ラベルと並び順。DecksPage / DeckBuilder などで共通利用する。
export const CIV_LABELS: Record<CardCivilization, string> = {
  light: "光",
  water: "水",
  darkness: "闇",
  fire: "火",
  nature: "自然",
};

export const CIV_ORDER: CardCivilization[] = [
  "light",
  "water",
  "darkness",
  "fire",
  "nature",
];

// 文明ピル（淡色・境界線タイプ）。文明色トークン（--civ-*）から生成。
export const civPillStyle = (civ: CardCivilization): React.CSSProperties => ({
  color: `var(--civ-${civ})`,
  borderColor: `color-mix(in oklch, var(--civ-${civ}) 45%, transparent)`,
  backgroundColor: `color-mix(in oklch, var(--civ-${civ}) 14%, transparent)`,
});

// 文明チップ（ソリッド塗り・白文字）。視認性が高く、デッキ一覧などで使う。
export const civChipStyle = (civ: CardCivilization): React.CSSProperties => ({
  color: "#fff",
  backgroundColor: `var(--civ-${civ})`,
});

// 文明ドット（色の丸だけ。文字を持たない最小表示）。
export const civDotStyle = (civ: CardCivilization): React.CSSProperties => ({
  backgroundColor: `var(--civ-${civ})`,
});

// 文明テキスト（文字だけを文明色で。地色・枠なし）。
export const civTextStyle = (civ: CardCivilization): React.CSSProperties => ({
  color: `var(--civ-${civ})`,
});

// 文明チップ（ソリッド塗り＋外周に背景色のリング。重なり表示でも境界が分かる）。
export const civChipRingStyle = (civ: CardCivilization): React.CSSProperties => ({
  color: "#fff",
  backgroundColor: `var(--civ-${civ})`,
  boxShadow: "0 0 0 1.5px var(--background)",
});

// 文明バー（左端に文明色の縦棒＋文字は前景色）。
export const civBarStyle = (civ: CardCivilization): React.CSSProperties => ({
  borderLeft: `3px solid var(--civ-${civ})`,
  backgroundColor: `color-mix(in oklch, var(--civ-${civ}) 12%, transparent)`,
});

// デッキ内容の並び順で使う文明順（単色: 自然→火→水→光→闇、その後に多色）。
export const DECK_SORT_CIV_ORDER: CardCivilization[] = [
  "nature",
  "fire",
  "water",
  "light",
  "darkness",
];

// カードの文明ランク。多色カードは単色カードより後（最後）にまとめる。
export function civRank(civs: CardCivilization[]): number {
  // 2文明以上の多色カードは末尾に置く。
  if (civs.length >= 2) return DECK_SORT_CIV_ORDER.length;
  const i = DECK_SORT_CIV_ORDER.indexOf(civs[0]);
  return i === -1 ? DECK_SORT_CIV_ORDER.length : i;
}

// デッキ内容の並び順比較: コスト → 文明 → ID の優先度。
export function compareDeckCards(
  a: { cost: number; civilization: CardCivilization[]; id: string },
  b: { cost: number; civilization: CardCivilization[]; id: string },
): number {
  return (
    a.cost - b.cost ||
    civRank(a.civilization) - civRank(b.civilization) ||
    a.id.localeCompare(b.id)
  );
}

// デッキに含まれる文明を並び順で返す。
export function getDeckCivs(cardIds: string[]): CardCivilization[] {
  const civSet = new Set<CardCivilization>();
  for (const id of cardIds) {
    getCardById(id)?.civilization.forEach((civ) => civSet.add(civ));
  }
  return CIV_ORDER.filter((c) => civSet.has(c));
}
